import { useState } from 'react';
import { Check, Copy, Download, Share2 } from 'lucide-react';
import { PlatformIcon } from '@/components/icons/PlatformIcon';
import { Modal } from '@/components/ui/Modal';
import { PLATFORMS } from '@/constants/platforms';
import { useDownloadAction } from '@/hooks/useDownloadAction';
import { useI18n } from '@/hooks/useI18n';
import { toast, useUiStore } from '@/store/uiStore';
import { formatBytes } from '@/utils/format';
import { copyToClipboard, shareUrl } from '@/utils/misc';
import type { OutputType } from '@/types';

const OUTPUT_LABEL: Record<OutputType, string> = {
  video: 'MP4 video',
  audio: 'MP3 audio',
  snapshots: 'Snapshot ZIP',
};

/**
 * Pops up the moment a job finishes so the user can download immediately —
 * no scrolling to the bottom of the page (important on mobile). Mirrors the
 * DownloadCard actions (download / copy / share).
 */
export function DownloadReadyModal() {
  const open = useUiStore((s) => s.downloadReadyOpen);
  const record = useUiStore((s) => s.readyRecord);
  const close = useUiStore((s) => s.closeDownloadReady);
  const startDownload = useDownloadAction();
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);

  const handleDownload = () => {
    if (!record?.downloadUrl) return;
    close();
    startDownload(record.downloadUrl, title);
  };

  const title = record?.metadata?.title ?? record?.sourceUrl ?? '';
  const platform = record ? PLATFORMS[record.platform] : null;

  const handleCopy = async () => {
    if (!record?.downloadUrl) return;
    if (await copyToClipboard(record.downloadUrl)) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleShare = async () => {
    if (!record?.downloadUrl) return;
    const result = await shareUrl({ title, url: record.downloadUrl });
    if (result === 'copied') toast({ title: t('common.copied'), variant: 'info' });
  };

  return (
    <Modal open={open} onClose={close} title="Your media is ready 🎉">
      {record && (
        <div className="space-y-5">
          <div className="flex items-center gap-3">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white/5 text-primary-300">
              {record.metadata?.thumbnailUrl ? (
                <img
                  src={record.metadata.thumbnailUrl}
                  alt=""
                  referrerPolicy="no-referrer"
                  className="h-full w-full object-cover"
                />
              ) : (
                <PlatformIcon platform={record.platform} className="h-6 w-6" />
              )}
            </div>
            <div className="min-w-0">
              <p className="line-clamp-2 text-sm font-semibold text-zinc-100">{title}</p>
              <p className="mt-1 flex items-center gap-1.5 text-xs text-zinc-500">
                {platform && (
                  <span style={{ color: platform.color }}>
                    <PlatformIcon platform={record.platform} className="h-3.5 w-3.5" />
                  </span>
                )}
                <span>{OUTPUT_LABEL[record.outputType]}</span>
                {record.fileSize != null && <span>· {formatBytes(record.fileSize)}</span>}
              </p>
            </div>
          </div>

          {record.downloadUrl && (
            <button
              type="button"
              onClick={handleDownload}
              className="flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-primary-500 to-accent-500 text-base font-semibold text-white shadow-glow transition-all hover:brightness-110 active:scale-[0.98] focus-ring"
            >
              <Download className="h-5 w-5" aria-hidden />
              {t('common.download')}
            </button>
          )}

          <div className="flex gap-2">
            <button
              onClick={handleCopy}
              className="flex h-11 flex-1 items-center justify-center gap-1.5 rounded-xl border border-white/10 bg-white/5 text-sm font-medium text-zinc-300 transition-colors hover:bg-white/10 focus-ring"
            >
              {copied ? (
                <Check className="h-4 w-4 text-emerald-400" aria-hidden />
              ) : (
                <Copy className="h-4 w-4" aria-hidden />
              )}
              {copied ? t('common.copied') : t('common.copy')}
            </button>
            <button
              onClick={handleShare}
              className="flex h-11 flex-1 items-center justify-center gap-1.5 rounded-xl border border-white/10 bg-white/5 text-sm font-medium text-zinc-300 transition-colors hover:bg-white/10 focus-ring"
            >
              <Share2 className="h-4 w-4" aria-hidden />
              {t('common.share')}
            </button>
          </div>

          <p className="text-center text-[11px] text-zinc-600">
            {t('common.expiresIn')} 24h · saved to your history below.
          </p>
        </div>
      )}
    </Modal>
  );
}
